import { isEmptyBlockContent } from 'shared-helpers/common';
import { getBlockTranslatableProperties } from 'shared-helpers/courses';

import type { Block, Course, PickUnion } from 'shared-types';

interface Params {
  block: PickUnion<Block, 'data' | 'type'>;
  courseDefaultLanguage: Course['language'];
}

interface TranslationProgress {
  [language: string]: number;
}

interface LocaleData {
  translatable: Record<string, string>;
}

const getBlockTranslationProgress = ({
  block,
  courseDefaultLanguage
}: Params): TranslationProgress => {
  const { data, type } = block;
  if (!data) return {};

  // verify that default language data exists
  if (!data.locales[courseDefaultLanguage]) {
    throw new Error(
      `Default language (${courseDefaultLanguage}) data is missing in ${type} block`
    );
  }

  const defaultLanguageData = data.locales[courseDefaultLanguage]?.translatable;

  const translatableProperties = getBlockTranslatableProperties({ type });

  const propertiesToCheck = [
    ...translatableProperties.static,
    ...Object.keys(defaultLanguageData).filter((key) =>
      translatableProperties.dynamic.some((pattern) => pattern.test(key))
    )
  ];
  if (!propertiesToCheck.length) {
    // if no pattern is defined for this block type, consider it 100% translated
    return Object.keys(data.locales).reduce((acc, language) => {
      if (language !== courseDefaultLanguage) {
        acc[language] = 100;
      }
      return acc;
    }, {} as TranslationProgress);
  }

  // filter out empty properties
  const nonEmptyProperties = propertiesToCheck.filter(
    (prop) =>
      !isEmptyBlockContent(
        defaultLanguageData[prop as keyof typeof defaultLanguageData] ?? ''
      )
  );
  if (nonEmptyProperties.length === 0) {
    // if there are no properties to check, all language versions are considered 100% translated
    return Object.keys(data.locales).reduce((acc, language) => {
      if (language !== courseDefaultLanguage) acc[language] = 100;
      return acc;
    }, {} as TranslationProgress);
  }

  // calculate progress for each language version
  const progress: TranslationProgress = {};
  Object.entries(data.locales).forEach(([language, localeData]) => {
    if (language === courseDefaultLanguage) return;

    // if the localization has its own file, consider it 100% translated
    if ('file' in localeData && localeData.file) {
      progress[language] = 100;
      return;
    }

    const translatable = (localeData as LocaleData)?.translatable || {};
    const translatedProperties = nonEmptyProperties.filter(
      (prop) => !isEmptyBlockContent(translatable[prop] ?? '')
    );

    // round down to the next integer
    progress[language] = Math.floor(
      (translatedProperties.length / nonEmptyProperties.length) * 100
    );
  });

  return progress;
};

export default getBlockTranslationProgress;
